"use client";

import moment from "moment";

// We need to import
import "moment/locale/es";
import { capitalizeFirstLetter } from "./string-ops";
import APP_CONFIGURATION from "@lib/app.config";
import { DEFAULT_LOCALE } from "@lib/constants";

// Helper function to format date to standardized ISO string
export const formatDateToISOWithoutTimezone = dateInput => {
  const date = new Date(dateInput);
  // Extract the local date components to avoid timezone issues
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based month
  const day = date.getDate();
  // This guarantees the date won't shift to the previous day
  const standardizedDate = new Date(Date.UTC(year, month, day, 18, 0, 0, 0));
  return standardizedDate.toISOString();
};

// Interface on js more descriptive

export const dateFromUTC = date => {
  return new Date(date.replace("Z", ""));
};
/**
 * Gives the hour and minutes of a datetime string
 * @param date datetime string with an hour and minutes value
 * @param format format string for the time display
 * @param deleteSpace whether to remove the space between time and AM/PM
 * @returns string with the hour and minutes in format: H:mm am/pm or H:mmam/pm
 */
export const getHourString = function (date) {
  let format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "h:mm a";
  let deleteSpace = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  const finalFormat = deleteSpace ? format.replace(" a", "a").replace(" A", "A") : format;
  return moment(date).format(finalFormat);
};
export const getHourStringWithTimezone = function (timeString) {
  let upperCase = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  // Parse the time string with the exact format
  const parsedTime = moment(timeString, "HH:mm:ss.SSSZ");
  // Check if the time is valid
  if (!parsedTime.isValid()) {
    return "Invalid time";
  }

  // Format the parsed time to display hour and minute with AM/PM
  return parsedTime.format("h:mm ".concat(upperCase ? "A" : "a"));
};

/**
 * Get the day
 * @param date
 * @returns
 */
export const getDayOfTheMonth = date => {
  const day = moment(date).format("D");
  return day;
};

/**
 * Gives a string with a date in the format: Weekday, Day Month, Year
 * @param date datetime string to be formatted
 * @returns
 */
export const getDateString = function (date) {
  let shortMonth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  let shortWeekDay = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  let hideWeekDay = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  moment.locale(localStorage.getItem("locale") || DEFAULT_LOCALE);
  const weekday = capitalizeFirstLetter(moment(date).format("ddd")).replaceAll(".", "");
  const day = moment(date).format("D");
  const monthFormat = shortMonth ? "MMM" : "MMMM";
  const month = capitalizeFirstLetter(moment(date).format(monthFormat)).replaceAll(".", "");
  const year = moment(date).format("YYYY");
  return hideWeekDay ? "".concat(day, " ").concat(month, ", ").concat(year) : "".concat(weekday, ", ").concat(day, " ").concat(month, ", ").concat(year);
};

/**
 * Gives a string with a date in the format: Day Month, Year
 * Preserves exact date components without timezone shifts
 * @param date datetime string to be formatted
 * @returns
 */
export const getShortDateString = function (date) {
  let hideYear = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  let fullFormat = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  moment.locale(localStorage.getItem("locale") || DEFAULT_LOCALE);
  const format = hideYear ? fullFormat ? "D MMMM" : "MMM D" : fullFormat ? "D MMMM, YYYY" : "MMM D, YYYY";

  // Parse date and extract components to avoid timezone shifts
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  const day = dateObj.getDate();

  // Create a moment object with exact date components
  const exactDate = moment([year, month, day]);
  return capitalizeFirstLetter(exactDate.format(format).replaceAll(".", ""));
};

/**
 * Gives a string with a date in the format: Weekday, Day Month
 * @param date datetime string to be formatted
 * @returns
 */
export const getShortDateWithWeekdayString = date => {
  moment.locale(localStorage.getItem("locale") || DEFAULT_LOCALE);
  const weekday = capitalizeFirstLetter(moment(date).format("dddd")).replaceAll(".", "");
  const day = moment(date).format("D");
  const month = capitalizeFirstLetter(moment(date).format("MMM")).replaceAll(".", "");
  return "".concat(weekday, ", ").concat(day, " ").concat(month);
};

/**
 * Gives a string with a date in the format: Weekday, Day Month
 * @param date datetime string to be formatted
 * @returns
 */
export const getFullDateWithWeekdayString = (date, t) => {
  moment.locale(localStorage.getItem("locale") || DEFAULT_LOCALE);
  const weekday = capitalizeFirstLetter(moment(date).format("dddd"));
  const day = moment(date).format("D");
  const month = capitalizeFirstLetter(moment(date).format("MMMM"));
  return "".concat(weekday, " ").concat(day, " ").concat(t("Shared.OF"), " ").concat(month);
};
export const getShortDateWithWeekday = function (date, t) {
  let hideYear = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  const locale = localStorage.getItem("locale") || DEFAULT_LOCALE;
  moment.locale(locale);
  const format = hideYear ? "ddd, MMM D" : "ddd, MMM D, YYYY";
  return capitalizeFirstLetter(moment(date).format(format)).replaceAll(".", "");
};
export const getMonthName = date => {
  moment.locale(localStorage.getItem("locale") || DEFAULT_LOCALE);
  return capitalizeFirstLetter(moment(date).utc().format("MMMM")).replaceAll(".", "");
};
export const getMonthYear = date => {
  moment.locale(localStorage.getItem("locale") || DEFAULT_LOCALE);
  return capitalizeFirstLetter(moment(date).format("MMMM YYYY"));
};

/**
 * Gives the duration between two dates in the format: {hours}h {minutes}m
 * @param date1
 * @param date2
 * @returns
 */
export const getDurationBetweenDates = (date1, date2) => {
  if (!date1 || !date2) {
    return "0h 0m";
  }
  const diff = new Date(date2).getTime() - new Date(date1).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor(diff / (1000 * 60) % 60);
  return "".concat(hours, "h ").concat(minutes, "m");
};

/**
 * formats a date string to the given format
 * @param date valid date string to be formatted
 * @param format valid moment.js format string
 * @returns date string formatted to the given format
 */
export const formatDate = function (date) {
  let format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : APP_CONFIGURATION.config.dateFormat;
  let currentFormat = arguments.length > 2 ? arguments[2] : undefined;
  moment.locale(localStorage.getItem("locale") || DEFAULT_LOCALE);
  let formattedDate = moment(date, currentFormat).format(format);

  // Removes dots from the formatted date and capitalizes the first letter of each word as appliaction standard
  return capitalizeFirstLetter(formattedDate).replaceAll(".", "");
};

/**
 * Checks if a date string is in a valid format and within reasonable bounds
 * @param date valid date string to be checked
 * @param format valid moment.js format string
 * @returns boolean indicating if the date is valid and reasonable
 */
export const isValidFormat = function (date) {
  let format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : APP_CONFIGURATION.config.dateFormat;
  moment.locale(localStorage.getItem("locale") || DEFAULT_LOCALE);
  const parsedDate = moment(date, format, true);

  // Check basic format validity
  if (!parsedDate.isValid()) {
    return false;
  }

  // Check if date is within reasonable bounds (between year 1900 and 100 years from now)
  const year = parsedDate.year();
  const currentYear = moment().year();
  const isReasonableYear = year >= 1900 && year <= currentYear + 100;
  return isReasonableYear;
};

/**
 * Checks if a date string is before another date string, both dates must be in same format
 * @param date valid date string to be checked
 * @param dateToCompare valid date string to compare
 * @param dateFormat valid moment.js format string in which the dates are formatted
 * @returns true if the date is before the date to compare
 */
export const dateIsBefore = (date, dateToCompare, dateFormat) => {
  const dateTimestamp = moment(date, dateFormat).valueOf();
  const dateToCompareTimestamp = moment(dateToCompare, dateFormat).valueOf();
  return dateTimestamp < dateToCompareTimestamp;
};

/**
 * Checks if a date string is before or equal to another date string, both dates must be in same format
 * @param date valid date string to be checked
 * @param dateToCompare valid date string to compare
 * @param dateFormat valid moment.js format string in which the dates are formatted
 * @returns true if the date is before the date to compare
 */
export const dateIsBeforeOrEqual = (date, dateToCompare, dateFormat) => {
  const dateTimestamp = moment(date, dateFormat).valueOf();
  const dateToCompareTimestamp = moment(dateToCompare, dateFormat).valueOf();
  return dateTimestamp <= dateToCompareTimestamp;
};

/**
 * Checks if a date string is after another date string, both dates must be in same format
 * @param date valid date string to be checked
 * @param dateToCompare valid date string to compare
 * @param dateFormat valid moment.js format string in which the dates are formatted
 * @returns true if the date is after the date to compare
 */
export const dateIsAfter = (date, dateToCompare, dateFormat) => {
  const dateTimestamp = moment(date, dateFormat).valueOf();
  const dateToCompareTimestamp = moment(dateToCompare, dateFormat).valueOf();
  return dateTimestamp > dateToCompareTimestamp;
};
export function addDaysToDate(date, daysToAdd) {
  return moment(date).add(daysToAdd, "days").startOf("day").toDate();
}

/**
 * Gets an interval with N days before and N days after the selected date
 * @param date valid momentjs format string
 * @param offset number of days before and after the selected date
 * @param dateFormat valid momentjs format string
 * @returns array of dates, with the selected date in the middle
 */
export const getDateInterval = (date, offset, dateFormat) => {
  const selectedDate = moment(date, dateFormat);
  const dates = [];
  for (let i = -offset; i <= offset; i++) {
    const date = moment(selectedDate);
    date.add(i, "days");
    dates.push(date.toString());
  }
  return dates;
};

/**
 * Extracts the hours and minutes from a date string.
 * @param dateString - The date string to extract the time from.
 * @returns An object of type Time with the hours and minutes.
 */
export const extractTime = dateString => {
  const date = moment(dateString);
  return {
    hours: date.hours(),
    minutes: date.minutes()
  };
};

/**
 * Compares two dates excluding the time.
 * @param date - The first date to compare.
 * @param dateToCompare - The second date to compare.
 * @returns True if the dates are equal, false otherwise.
 */
export const datesAreEqual = (date, dateToCompare) => {
  const date1 = moment(date).startOf("day");
  if (!dateToCompare) {
    return false;
  }
  const date2 = moment(dateToCompare).startOf("day");
  return date1.isSame(date2);
};
export const yearAreEqual = (date, dateToCompare) => {
  if (!dateToCompare || !date) {
    return false;
  }
  const date1 = moment(date).startOf("year");
  const date2 = moment(dateToCompare).startOf("year");
  return date1.isSame(date2);
};

/**
 * Compares two dates including the time.
 * @param date - The first date to compare.
 * @param dateToCompare - The second date to compare.
 * @returns True if the dates and times are equal, false otherwise.
 */
export const datetimesAreEqual = (date, dateToCompare) => {
  const date1 = moment(date);
  const date2 = moment(dateToCompare);
  return date1.isSame(date2);
};

/**
 * Compares two dates excluding the time.
 * @param date - The first date to compare.
 * @param dateToCompare - The second date to compare.
 * @returns True if the first date is before the second date, false otherwise.
 */

export const formatDateToRange = (date, t) => {
  const monthKeys = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"];
  const translatedMonths = monthKeys.map(key => t("Shared.MONTHS.".concat(key)));
  const day = moment(date).format("DD");
  const monthIndex = Number(moment(date).format("M")) - 1;
  const formattedDate = "".concat(day, " ").concat(t("Shared.OF"), " ").concat(translatedMonths[monthIndex]);
  return formattedDate;
};
export const getLayoverTime = (departureTime, arrivalTime) => {
  const departure = moment(departureTime, "h:mm a");
  const arrival = moment(arrivalTime, "h:mm a");
  let layoverMinutes = departure.diff(arrival, "minutes");
  if (layoverMinutes < 0) {
    layoverMinutes += 1440;
  }
  const hours = Math.floor(layoverMinutes / 60);
  const minutes = layoverMinutes % 60;
  return "".concat(hours, "h ").concat(minutes, "min");
};

/**
 * Gets the difference in years between two dates
 * @param date1 date string
 * @param date2 date string
 * @returns difference in years between date1 and date2
 */
export const getDifferenceInYears = (date1, date2) => {
  const years = moment(date1).diff(date2, "years");
  return years;
};

/**
 * Gets the date in the format given date string
 * @returns Date
 */
export const getDateFromDateString = (date, format) => {
  const result = moment(date, format).toDate();
  return result;
};

/**
 * Calculates the total flight time in hours and minutes.
 *
 * @param totalTime - The total time in the format "HH:MM".
 * @returns The total flight time in the format "Xh Ymin".
 */
export const getTotalFlightTime = totalTime => {
  const [hours, minutes] = totalTime.split(":").map(Number);
  return "".concat(hours, "h ").concat(minutes, "min");
};

/**
 * Formats the given date and time strings into a specific format.
 * @param dateString - The date string in the format "YYYY/MM/DD".
 * @param timeString - The time string in the format "HH:mm:ss".
 * @returns The formatted date and time string in the format "DD MMM, h:mm a".
 */

export const formatDateTime = (dateString, timeString, dot, fullTime) => {
  const date = moment(dateString, "YYYY/MM/DD");
  const formattedDate = date.format("DD MMM").replace(".", "");
  const hourFormat = fullTime ? undefined : "HH:mm:ss.SSSZ";
  const formattedTime = moment(timeString, hourFormat).format("h:mm A");
  if (dot) {
    return "".concat(formattedDate, " \u2022 ").concat(formattedTime, " ");
  } else {
    return "".concat(formattedDate, ", ").concat(formattedTime);
  }
};
export const dateCompletionValidator = date => {
  const dateMoment = moment(date);
  const currentDate = moment();
  return dateMoment.isBefore(currentDate);
};
export const isDateBetween = (date, startDate, endDate) => {
  if (!startDate || !endDate) {
    return false;
  }
  if (!endDate) {
    return moment(date).isSameOrAfter(startDate);
  }
  return moment(date).isBetween(startDate, endDate, undefined, "[]");
};

/**
 * Returns the number of days between two Date objects.
 * @param {Date} startDate - The start date.
 * @param {Date} endDate - The end date.
 * @returns {number} - The number of days between the dates.
 */
export const countDaysBetweenDates = (startDate, endDate) => {
  const timeDifference = endDate.getTime() - startDate.getTime();
  const daysDifference = timeDifference / (1000 * 3600 * 24);
  return Math.ceil(daysDifference);
};